import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ClubMember } from '../../types/clubs';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

interface MemberCardProps {
  member: ClubMember;
  onPress: () => void;
  showRemoveButton?: boolean;
  onRemovePress?: () => void;
}

const MemberCard: React.FC<MemberCardProps> = ({ 
  member, 
  onPress, 
  showRemoveButton = false,
  onRemovePress 
}) => {
  const { theme } = useTheme();
  const handleRemovePress = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onRemovePress?.();
  };

  return (
    <TouchableOpacity style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]} onPress={onPress} activeOpacity={0.7}>
      <View style={[styles.avatar, { backgroundColor: theme.accent }]}>
        <Ionicons name="person" size={28} color={theme.textInverse} />
      </View>
      
      <View style={styles.info}>
        <View style={styles.nameRow}>
          <ThemedText style={[styles.name, { color: theme.text }]}>{member.name}</ThemedText>
          {member.role === 'admin' && (
            <View style={[styles.adminBadge, { backgroundColor: theme.backgroundSecondary, borderColor: theme.accent }]}>
              <ThemedText style={[styles.adminText, { color: theme.accent }]}>Admin</ThemedText>
            </View>
          )}
        </View>
        <ThemedText style={[styles.profession, { color: theme.textSecondary }]}>{member.profession}</ThemedText>
        <View style={styles.locationRow}>
          <Ionicons name="location" size={12} color={theme.textTertiary} />
          <ThemedText style={[styles.city, { color: theme.textTertiary }]}>{member.city}</ThemedText>
        </View>
      </View>

      {showRemoveButton && member.role !== 'admin' && (
        <TouchableOpacity 
          style={styles.removeButton}
          onPress={handleRemovePress}
          activeOpacity={0.7}
        >
          <Ionicons name="close-circle" size={24} color="#DC3545" />
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    marginHorizontal: 16,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
  },
  info: {
    flex: 1,
    marginLeft: 12,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  name: {
    fontSize: 15,
    fontWeight: '600',
  },
  adminBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
  },
  adminText: {
    fontSize: 10,
    fontWeight: '600',
  },
  profession: {
    fontSize: 13,
    marginBottom: 2,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  city: {
    fontSize: 12,
  },
  removeButton: {
    padding: 4,
  },
});

export default MemberCard;
